import { useState } from "react";
import { Plus } from "lucide-react";
import { useApi, Badge } from "@/pages/admin/regie/ui";
import { fmtMoney, MARKET_LABEL } from "@/lib/market";
import { STATUS_LABEL } from "@/pages/public/ExperiencesPage";
import ExperienceEditor from "@/pages/admin/ExperienceEditor";

const TONE = { LIVE: "lime", SCHEDULED: "sky", DRAFT: "slate", CANCELLED: "red", WINNER_PENDING: "amber" };

const Money = ({ m, testId }) => {
  const cur = Object.keys(m || {});
  if (!cur.length) return <span className="text-slate-500">—</span>;
  return <span className="space-y-0.5" data-testid={testId}>{cur.map((c) => (
    <span key={c} className="block font-mono-num">{fmtMoney(m[c].revenue_cents, c)} − {fmtMoney(m[c].cost_cents, c)} = <b className={m[c].margin_cents >= 0 ? "text-[#B7F52A]" : "text-red-300"}>{fmtMoney(m[c].margin_cents, c)}</b></span>))}</span>;
};

function Summary({ market, onExpired }) {
  const [s] = useApi(`/superadmin/experiences/summary?market=${market}`, onExpired);
  if (!s) return null;
  const k = [["Experiences", s.experiences], ["En cours", s.live], ["Inscriptions", s.registrations], ["Joueurs actifs", s.active_players], ["Gagnants", s.winners], ["Lots remis", s.prizes_delivered]];
  return (
    <div className="grid gap-2 sm:grid-cols-3 lg:grid-cols-6" data-testid="exp-summary">
      {k.map(([l, v]) => <div key={l} className="mpl-card p-3"><p className="text-[10px] uppercase text-slate-400">{l}</p><p className="font-display text-2xl font-extrabold" data-testid={`exp-kpi-${l}`}>{v}</p></div>)}
      <div className="mpl-card p-3 text-xs sm:col-span-3 lg:col-span-6"><p className="mb-1 text-[10px] uppercase text-slate-400">Revenus − coûts = marge (par devise, hors démo)</p><Money m={s.money} testId="exp-kpi-money" /></div>
    </div>
  );
}

export default function ExperiencesAdmin({ onExpired }) {
  const [market, setMarket] = useState("ALL");
  const [status, setStatus] = useState("");
  const [rows, load] = useApi(`/superadmin/experiences?market=${market}${status ? `&status=${status}` : ""}`, onExpired);
  const [open, setOpen] = useState(null);
  if (open) return <ExperienceEditor id={open === "new" ? null : open} onClose={() => { setOpen(null); load(); }} onExpired={onExpired} />;
  return (
    <div className="space-y-4" data-testid="experiences-admin">
      <div className="flex flex-wrap items-center gap-2">
        <select className="mpl-input !w-auto" value={market} onChange={(e) => setMarket(e.target.value)} data-testid="exp-filter-market"><option value="ALL">Tous marchés</option><option value="FR">France</option><option value="CA">Canada</option></select>
        <select className="mpl-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="exp-filter-status"><option value="">Tous statuts actifs</option>{Object.entries(STATUS_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
        <button className="mpl-btn-primary" onClick={() => setOpen("new")} data-testid="exp-new"><Plus size={14} /> Nouvelle Experience</button>
      </div>
      <Summary market={market} onExpired={onExpired} />
      <ul className="space-y-2">{(rows || []).map((e) => (
        <li key={e.id}><button onClick={() => setOpen(e.id)} className="mpl-card grid w-full gap-2 p-3 text-left text-xs md:grid-cols-[2fr_1fr_1fr_2fr]" data-testid={`exp-row-${e.slug}`}>
          <span><span className="block text-sm font-bold">{e.name}</span><span className="flex flex-wrap gap-1 pt-1"><Badge tone={TONE[e.status] || "slate"}>{STATUS_LABEL[e.status]}</Badge><Badge tone="sky">{MARKET_LABEL[e.market]}</Badge>{e.is_demo && <Badge tone="amber">Démo</Badge>}</span></span>
          <span className="text-slate-300">{e.launch_at ? new Date(e.launch_at).toLocaleDateString("fr-FR") : "—"} → {e.end_at ? new Date(e.end_at).toLocaleDateString("fr-FR") : "—"}<span className="block text-slate-500">{e.sponsors.length} partenaire(s)</span></span>
          <span className="text-slate-300">{e.registrations} inscrits · {e.active_players} actifs<span className="block text-slate-500">{e.league_id ? "Ligue liée" : "Sans ligue"} · {(e.winners || []).length} gagnant(s)</span></span>
          <Money m={e.money} />
        </button></li>))}
        {rows && !rows.length && <li className="text-sm text-slate-400" data-testid="exp-admin-empty">Aucune Experience pour ces filtres.</li>}
      </ul>
    </div>
  );
}
